import { StructureAttribute } from '../../../../../@types';
import { ServiceAddressAttributeValue } from '../../../../../@types/service.type';
import { GOOGLE_MAP_URL } from '../../../../../configs/url-constants';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKENDS = ['Saturday', 'Sunday'];

export const formatAddress = (address: ServiceAddressAttributeValue | undefined): string => {
  if (!address) return '';
  const parts = [
    address.line1,
    address.line2,
    address.suburb,
    address.stateName || address.state,
    address.postCode,
  ].filter(Boolean);
  return parts.join(', ');
};

export const buildGoogleMapsUrl = (address: ServiceAddressAttributeValue | undefined): string => {
  const formatted = formatAddress(address);
  if (!formatted) return '';
  return `${GOOGLE_MAP_URL}?q=${encodeURIComponent(formatted)}`;
};

export const buildEmbedUrl = (address: ServiceAddressAttributeValue | undefined): string => {
  const formatted = formatAddress(address);
  if (!formatted) return '';
  return `${GOOGLE_MAP_URL}?q=${encodeURIComponent(formatted)}&output=embed`;
};

export interface DayHours {
  day: string;
  start?: string;
  end?: string;
  hasHours: boolean;
}

export const buildAllDayHours = (tradingHours: StructureAttribute[] | undefined): DayHours[] => {
  const hoursMap: Record<string, { start: string; end: string }> = {};
  if (tradingHours) {
    for (const th of tradingHours) {
      if (th.value?.type) {
        hoursMap[th.value.type] = { start: th.value.start, end: th.value.end };
      }
    }
  }
  return ALL_DAYS.map((day) => {
    const hours = hoursMap[day];
    return hours
      ? { day, start: hours.start, end: hours.end, hasHours: true }
      : { day, hasHours: false };
  });
};

const expandCoverageType = (type: string): string[] => {
  switch (type) {
    case 'All Days': return ALL_DAYS;
    case 'Weekdays': return WEEKDAYS;
    case 'Weekends': return WEEKENDS;
    default: return ALL_DAYS.includes(type) ? [type] : [];
  }
};

export const buildCoverageHours = (coverages: StructureAttribute[] | undefined): DayHours[] => {
  const enabled = coverages?.filter((c) => c.value?.checked) || [];
  const dayMap: Record<string, { start: string; end: string }> = {};
  const extraRows: DayHours[] = [];

  for (const cov of enabled) {
    const th = cov.value?.tradingHours;
    if (!th?.type) continue;

    const hours = { start: th.start, end: th.end };
    const days = expandCoverageType(th.type);

    if (days.length > 0) {
      for (const day of days) {
        if (!dayMap[day]) dayMap[day] = hours;
      }
    }

    if (th.type === 'Public Holiday' || th.type === 'Individual Date') {
      extraRows.push({ day: th.type, start: th.start, end: th.end, hasHours: true });
    }
  }

  const dayRows = ALL_DAYS.map((day) => {
    const hours = dayMap[day];
    return hours
      ? { day, start: hours.start, end: hours.end, hasHours: true }
      : { day, hasHours: false };
  });

  return [...dayRows, ...extraRows];
};
